/**
 * La carte de l'application — un seul endroit, lu par le menu et par la
 * palette de création.
 *
 * Le plan « Le coût en clics » comptait seize entrées dans le menu : sur une
 * fenêtre de 646 px, six ne s'atteignaient qu'en faisant défiler, sans que
 * rien ne l'indique. L'onglet Stock posé le 11 septembre était invisible le
 * lendemain.
 *
 * Huit groupes, rangés par **la question qu'on se pose** — vendre, encaisser,
 * produire, acheter — et non par les tables de la base. Et la règle qui rend
 * le regroupement gratuit, sans laquelle il ajouterait le clic qu'on voulait
 * retirer : **le libellé du groupe est un lien vers son enfant le plus
 * fréquent**, et les frères se déplient dès qu'on est dans la section. Le cas
 * courant ne coûte rien de plus ; le cas rare cesse d'être invisible.
 *
 * Aucune destination n'est retirée : les seize sont toutes là.
 */

export interface NavChild {
  to: string;
  label: string;
}

export interface NavGroup {
  id: string;
  label: string;
  /** Où mène le libellé du groupe : l'enfant le plus fréquent. */
  to: string;
  /** Vide pour un groupe qui n'est qu'une destination (Pilotage, Alertes). */
  children: NavChild[];
}

export const NAV_GROUPS: readonly NavGroup[] = [
  { id: "pilotage", label: "Pilotage", to: "/", children: [] },
  {
    id: "vendre",
    label: "Vendre",
    to: "/devis",
    children: [
      { to: "/devis", label: "Devis" },
      { to: "/clients", label: "Clients" },
    ],
  },
  {
    id: "encaisser",
    label: "Encaisser",
    to: "/factures",
    children: [
      { to: "/factures", label: "Factures" },
      { to: "/e-factures", label: "E-factures" },
    ],
  },
  {
    id: "produire",
    label: "Produire",
    to: "/chantiers",
    children: [
      { to: "/chantiers", label: "Chantiers" },
      { to: "/planning", label: "Planning" },
      { to: "/sous-traitants", label: "Sous-traitants" },
    ],
  },
  {
    id: "equipe",
    label: "Équipe",
    to: "/equipe",
    children: [
      { to: "/equipe", label: "Salariés" },
      { to: "/pointage", label: "Pointage" },
    ],
  },
  {
    id: "acheter",
    label: "Acheter",
    to: "/depenses",
    children: [
      { to: "/depenses", label: "Dépenses" },
      { to: "/achats", label: "Factures d'achat" },
      { to: "/commandes", label: "Commandes" },
      { to: "/fournisseurs", label: "Fournisseurs" },
    ],
  },
  {
    id: "materiel",
    label: "Matériel",
    to: "/stock",
    children: [
      { to: "/materiel", label: "Outils et machines" },
      { to: "/stock", label: "Stock" },
      { to: "/catalogue", label: "Catalogue" },
    ],
  },
  { id: "alertes", label: "Alertes", to: "/alertes", children: [] },
];

/** `/devis/12/edit` appartient à `/devis` ; `/` n'appartient qu'à lui-même. */
function sousChemin(pathname: string, to: string): boolean {
  if (to === "/") return pathname === "/";
  return pathname === to || pathname.startsWith(`${to}/`);
}

/**
 * Le groupe de la page ouverte, ou `null` (Paramètres, une page hors menu).
 *
 * Cherché sur les enfants **et** sur la cible du groupe : une fiche de devis
 * ouverte déplie « Vendre », une fiche client aussi.
 */
export function activeGroup(pathname: string): NavGroup | null {
  return (
    NAV_GROUPS.find(
      (g) => sousChemin(pathname, g.to) || g.children.some((c) => sousChemin(pathname, c.to)),
    ) ?? null
  );
}

/**
 * L'enfant actif, au plus long préfixe.
 *
 * Le plus long, parce que deux enfants peuvent partager un début : sans cette
 * règle, une page `/factures-x` future allumerait « Factures ».
 */
export function activeChild(pathname: string, group: NavGroup): NavChild | null {
  const candidats = group.children.filter((c) => sousChemin(pathname, c.to));
  return candidats.sort((a, b) => b.to.length - a.to.length)[0] ?? null;
}

/** Toutes les destinations, pour la palette : aucune n'est cachée. */
export function allDestinations(): { to: string; label: string; group: string }[] {
  const vues = new Set<string>();
  const out: { to: string; label: string; group: string }[] = [];
  for (const g of NAV_GROUPS) {
    const liste = g.children.length > 0 ? g.children : [{ to: g.to, label: g.label }];
    for (const c of liste) {
      if (vues.has(c.to)) continue;
      vues.add(c.to);
      out.push({ ...c, group: g.label });
    }
  }
  out.push({ to: "/parametres", label: "Paramètres", group: "Réglages" });
  return out;
}

/**
 * Filtre souple : chaque mot tapé doit se retrouver, sans accents ni casse.
 * « fact ach » trouve « Factures d'achat » ; « depense » trouve « Dépenses ».
 */
export function matchesQuery(text: string, query: string): boolean {
  // NFD sépare la lettre de son accent ; la classe Unicode retire l'accent.
  const plat = (s: string) =>
    s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
  const cible = plat(text);
  return plat(query)
    .split(/\s+/)
    .filter(Boolean)
    .every((mot) => cible.includes(mot));
}
